import AgentChatMessage from '@/components/AgentChatMessage';
import { useAgentChat } from '@/hooks/useAgentChat';
import { deleteConversation, loadConversation, useConversations } from '@/services/agent/hooks';
import { agentKeys } from '@/services/agent/hooks';
import useAgentChatStore from '@/stores/useAgentChatStore';
import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  HStack,
  IconButton,
  Input,
  Text,
  Tooltip,
  VStack,
} from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';

const SUGGESTED_PROMPTS = [
  'Plan my week',
  "What's incomplete today?",
  'Add an action',
  'Show my categories',
  'Weekly summary',
];

function AgentChat() {
  const { isOpen, messages, conversationId, isSending, startNewConversation } = useAgentChatStore();
  const { setIsOpen } = useAgentChatStore();
  const { sendMessage, stopGeneration } = useAgentChat();
  const [inputValue, setInputValue] = useState('');
  const [showConversations, setShowConversations] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();

  const { data: conversations } = useConversations(isOpen);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when drawer opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const handleSend = useCallback(() => {
    if (inputValue.trim()) {
      sendMessage(inputValue.trim());
      setInputValue('');
    }
  }, [inputValue, sendMessage]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend],
  );

  const handlePromptClick = useCallback(
    (prompt: string) => {
      sendMessage(prompt);
    },
    [sendMessage],
  );

  const handleLoadConversation = useCallback(async (id: string) => {
    await loadConversation(id);
    setShowConversations(false);
  }, []);

  const handleDeleteConversation = useCallback(
    async (id: string, e: React.MouseEvent) => {
      e.stopPropagation();
      await deleteConversation(id);
      queryClient.invalidateQueries({ queryKey: agentKeys.conversations });
      if (conversationId === id) {
        startNewConversation();
      }
    },
    [conversationId, queryClient, startNewConversation],
  );

  const handleNewConversation = useCallback(() => {
    startNewConversation();
    setShowConversations(false);
  }, [startNewConversation]);

  return (
    <>
      {/* Floating chat button — positioned bottom-left (Steno is on the right) */}
      <Tooltip label="RPM AI Agent" placement="right">
        <IconButton
          aria-label="Open RPM AI Agent"
          position="fixed"
          bottom={6}
          left={6}
          zIndex={999}
          size="lg"
          borderRadius="full"
          bg="blue.500"
          color="white"
          _hover={{ bg: 'blue.600', transform: 'scale(1.05)' }}
          _active={{ bg: 'blue.700' }}
          boxShadow="lg"
          onClick={() => setIsOpen(true)}
          icon={
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              <circle cx="12" cy="10" r="1" fill="currentColor" />
              <circle cx="8" cy="10" r="1" fill="currentColor" />
              <circle cx="16" cy="10" r="1" fill="currentColor" />
            </svg>
          }
        />
      </Tooltip>

      {/* Chat drawer */}
      <Drawer isOpen={isOpen} placement="left" onClose={() => setIsOpen(false)} size="md">
        <DrawerOverlay />
        <DrawerContent bg="gray.900" color="white">
          <DrawerCloseButton />
          <DrawerHeader borderBottomWidth="1px" borderColor="gray.700">
            <Flex align="center" justify="space-between" pr={8}>
              <HStack spacing={3}>
                <Box w={2} h={2} borderRadius="full" bg="green.400" />
                <Text fontSize="lg" fontWeight="bold">
                  RPM AI
                </Text>
              </HStack>
              <HStack spacing={2}>
                <Button
                  size="xs"
                  variant="ghost"
                  colorScheme="whiteAlpha"
                  onClick={() => setShowConversations(!showConversations)}
                >
                  History
                </Button>
                <Button size="xs" variant="ghost" colorScheme="whiteAlpha" onClick={handleNewConversation}>
                  New
                </Button>
              </HStack>
            </Flex>
          </DrawerHeader>

          <DrawerBody p={0}>
            {showConversations ? (
              /* Conversation list */
              <VStack align="stretch" p={4} spacing={2}>
                {conversations && conversations.length > 0 ? (
                  conversations.map((conv: { id: string; title: string; created_at: string }) => (
                    <Flex
                      key={conv.id}
                      p={3}
                      bg="gray.800"
                      borderRadius="md"
                      cursor="pointer"
                      _hover={{ bg: 'gray.700' }}
                      onClick={() => handleLoadConversation(conv.id)}
                      justify="space-between"
                      align="center"
                    >
                      <Box flex={1} overflow="hidden">
                        <Text fontSize="sm" fontWeight="medium" noOfLines={1}>
                          {conv.title || 'Untitled'}
                        </Text>
                        <Text fontSize="xs" color="gray.400">
                          {new Date(conv.created_at).toLocaleDateString()}
                        </Text>
                      </Box>
                      <IconButton
                        aria-label="Delete conversation"
                        icon={
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                          </svg>
                        }
                        size="xs"
                        variant="ghost"
                        colorScheme="red"
                        onClick={(e) => handleDeleteConversation(conv.id, e)}
                      />
                    </Flex>
                  ))
                ) : (
                  <Text fontSize="sm" color="gray.500" textAlign="center" py={8}>
                    No conversations yet
                  </Text>
                )}
              </VStack>
            ) : (
              /* Chat messages */
              <Flex direction="column" h="full" p={4}>
                {messages.length === 0 ? (
                  /* Empty state with suggested prompts */
                  <Flex direction="column" flex={1} justify="center" align="center" gap={4}>
                    <Text fontSize="xl" fontWeight="bold" color="gray.300">
                      RPM AI
                    </Text>
                    <Text fontSize="sm" color="gray.500" textAlign="center" maxW="280px">
                      Your AI planning assistant powered by the RPM methodology
                    </Text>
                    <VStack spacing={2} mt={4} w="full" maxW="300px">
                      {SUGGESTED_PROMPTS.map((prompt) => (
                        <Button
                          key={prompt}
                          size="sm"
                          variant="outline"
                          colorScheme="whiteAlpha"
                          w="full"
                          onClick={() => handlePromptClick(prompt)}
                          isDisabled={isSending}
                        >
                          {prompt}
                        </Button>
                      ))}
                    </VStack>
                  </Flex>
                ) : (
                  /* Messages */
                  <Box flex={1} overflowY="auto" mb={2}>
                    {messages.map((msg) => (
                      <AgentChatMessage
                        key={msg.id}
                        role={msg.role}
                        content={msg.content}
                        isStreaming={msg.isStreaming}
                      />
                    ))}
                    <div ref={messagesEndRef} />
                  </Box>
                )}
              </Flex>
            )}
          </DrawerBody>

          <DrawerFooter borderTopWidth="1px" borderColor="gray.700" p={3}>
            <HStack w="full" spacing={2}>
              <Input
                ref={inputRef}
                placeholder="Ask RPM AI..."
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                bg="gray.800"
                border="1px solid"
                borderColor="gray.600"
                _focus={{ borderColor: 'blue.400' }}
                _placeholder={{ color: 'gray.500' }}
                isDisabled={isSending}
              />
              {isSending ? (
                <IconButton
                  aria-label="Stop"
                  colorScheme="red"
                  size="md"
                  onClick={stopGeneration}
                  icon={
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <rect x="6" y="6" width="12" height="12" rx="2" />
                    </svg>
                  }
                />
              ) : (
                <IconButton
                  aria-label="Send"
                  colorScheme="blue"
                  size="md"
                  onClick={handleSend}
                  isDisabled={!inputValue.trim()}
                  icon={
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="22" y1="2" x2="11" y2="13" />
                      <polygon points="22 2 15 22 11 13 2 9 22 2" />
                    </svg>
                  }
                />
              )}
            </HStack>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </>
  );
}

export default AgentChat;
